import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'wouter';
import ActionsMenu from './ActionsMenu';
import useDocumentTitle from '../../../../hooks/useDocumentTitle';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import useKeyPress, { ESCAPE_KEY } from '../../../../hooks/useKeyPress';
import ConfirmDialog from '../../../ConfirmDialog';
import { BluecoinRecord } from '../BluecoinsListState';
import { URL_BLUECOINS } from '../../../../router/constants';
import BluecoinDetail from '../../Detail';
import UseCreationSettings from '../../UseCreationSettings';
import { BLE_TABLE_NAME, IS_OK } from '../../constants';
import { discard } from '../../BluecoinsService';
import useAnalytics from '../../../../hooks/useAnalytics';
import { getTableName } from '../../../CmdTable/CmdTableService';

export interface IActionsProps<T extends BluecoinRecord> {
  data: T;
  updateTable: () => void;
}

declare global {
  interface Window {
    resizeTimeout?: number;
  }
}

function Actions<T extends BluecoinRecord>(props: IActionsProps<T>): JSX.Element {
  const [pathname, navigate] = useLocation();
  const [isActionsMenuOpened, setIsActionsMenuOpened] = React.useState(false);
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const { formatMessage: f } = useIntl();
  const { setDocumentTitle } = useDocumentTitle();
  const [isDiscardConfirmDialogOpen, setIsDiscardConfirmDialogOpen] =
    React.useState<boolean>(false);
  const [isMenuVisible, setIsMenuVisible] = React.useState(true);
  const menuRef: React.RefObject<HTMLDivElement> = React.createRef();
  const clickOutsideRef: React.RefObject<HTMLDivElement> = React.createRef();
  const lastPosition = React.useRef<{ top: number; left: number }>({ top: 0, left: 0 });
  const [itemId, setItemId] = React.useState<string | null>(null);
  const [isCreationSettingsOpen, setIsCreationSettingsOpen] = React.useState<boolean>(false);
  const { trackEvent } = useAnalytics();

  useOutsideClick(
    clickOutsideRef,
    (e) => {
      setIsActionsMenuOpened(false);
    },
    [menuRef]
  );

  useKeyPress(ESCAPE_KEY, () => {
    setIsActionsMenuOpened(false);
  });

  const isElementVisible = React.useCallback((): boolean => {
    const rect = clickOutsideRef.current?.getBoundingClientRect();
    const header = document.getElementById('cmd-table-header');
    const table = document.getElementById('cmd-table');

    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    const tableTop = table ? table.getBoundingClientRect().top : 0;
    const tableBottom = table ? table.getBoundingClientRect().bottom : 0;

    return !!(
      rect &&
      rect.top >= headerBottom && // Ensure the top of the element is below the bottom of the header
      rect.bottom <= tableBottom && // Ensure the bottom of the element is above the bottom of the table
      rect.right > 0 &&
      rect.left < (window.innerWidth || document.documentElement.clientWidth)
    );
  }, [clickOutsideRef]);

  const updateMenuPosition = React.useCallback(() => {
    if (clickOutsideRef.current) {
      const rect = clickOutsideRef.current.getBoundingClientRect();
      const table = document.getElementById('cmd-table');
      const tableBottom = table ? table.getBoundingClientRect().bottom : 0;
      const menuHeight = 176;

      const newPosition = {
        top: rect.bottom + 9 + window.scrollY,
        left: rect.right - 129 + window.scrollX,
      };

      if (rect.bottom + menuHeight > tableBottom) {
        newPosition.top = rect.top - menuHeight + window.scrollY;
      }

      // Only update if position actually changes
      if (
        newPosition.top !== lastPosition.current.top ||
        newPosition.left !== lastPosition.current.left
      ) {
        setMenuPosition(newPosition);
        lastPosition.current = newPosition; // Update the last known position
      }
    }
  }, [clickOutsideRef]);

  const handleResizeOrScroll = React.useCallback(() => {
    clearTimeout(window.resizeTimeout);
    window.resizeTimeout = window.setTimeout(() => {
      if (isActionsMenuOpened) {
        if (isElementVisible()) {
          updateMenuPosition();
          setIsMenuVisible(true);
        } else {
          setIsMenuVisible(false);
          // setIsActionsMenuOpened(false);
        }
      }
    }, 100);
  }, [isActionsMenuOpened, updateMenuPosition, isElementVisible]);

  React.useEffect(() => {
    window.addEventListener('scroll', handleResizeOrScroll);
    window.addEventListener('resize', handleResizeOrScroll);

    // Initial position update
    if (isActionsMenuOpened) {
      updateMenuPosition();
      setIsMenuVisible(isElementVisible());
    }

    return () => {
      window.removeEventListener('scroll', handleResizeOrScroll);
      window.removeEventListener('resize', handleResizeOrScroll);
      clearTimeout(window.resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleResizeOrScroll, isActionsMenuOpened, updateMenuPosition]);

  const handleActions = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Open actions menu']);

    const newState = !isActionsMenuOpened;
    setIsActionsMenuOpened(newState);
    if (newState) {
      const isVisible = isElementVisible();
      setIsMenuVisible(isVisible);
      if (isVisible) {
        updateMenuPosition();
      }
    }
  };

  const handleDiscardConfirmDialog = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Discard confirm dialog']);

    setIsActionsMenuOpened(false);
    setIsDiscardConfirmDialogOpen(true);
  };

  const handleCancelDiscard = () => {
    trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Cancel confirm dialog']);
    setIsDiscardConfirmDialogOpen(false);
  };

  const handleSubmitConfirmDialog = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Discard submit']);
    const data = await discard([props.data.id]);
    if (data && data.status === IS_OK) {
      props.updateTable();
      setIsDiscardConfirmDialogOpen(false);
    }
  };

  const handleOpenModal = () => {
    trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Open detail modal']);
    setItemId(String(props.data.id));
    navigate(`${URL_BLUECOINS}/detail/${props.data.id}`);
  };

  const handleCloseMenu = () => {
    setIsActionsMenuOpened(false);
    trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Close actions menu']);
  };
  const handleOpenUseCreationSettingsModal = () => {
    setIsCreationSettingsOpen(true);
    trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Open use creation settings']);
  };

  const handleCloseModal = () => {
    trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Close detail modal']);
    setItemId(null);
    navigate(`${URL_BLUECOINS}`);
    const documentTitle = `${f({ id: 'table_list.tabs.bluecoin.list' })} - ${f({
      id: 'menu.bluecoins',
    })}`;
    setDocumentTitle(documentTitle);
  };

  const handleCloseUseCreationSettingsModal = () => {
    trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Close use creation settings']);
    setItemId(null);
    setIsCreationSettingsOpen(false);
  };

  return (
    <div
      ref={clickOutsideRef}
      style={{
        zIndex: isActionsMenuOpened ? 10 : 4,
        margin: 'auto',
      }}
      key={props.data.id}
    >
      <div className="">
        <a
          href="#"
          className="text-gray-lighten"
          onClick={handleActions}
          style={{ padding: '15px', margin: '-15px' }}
          id={`rideActions-${props.data.id}`}
          data-cy="actions-menu-ride"
        >
          <i className="cmd-icons cmd-icon-more-horizontal fs-4" id={`ride-${props.data.id}`} />
          <span className="sr-only">{f({ id: 'table.actionsBtn' })}</span>
        </a>
        {isActionsMenuOpened && isMenuVisible && (
          <ActionsMenu
            data={props.data}
            showDetailModalCallback={handleOpenModal}
            showDiscardConfirmModalCallback={handleDiscardConfirmDialog}
            closeCallback={handleCloseMenu}
            position={menuPosition}
            ref={menuRef}
            showUseCreationSettingsModalCallback={handleOpenUseCreationSettingsModal}
          />
        )}
      </div>
      {itemId && itemId === String(props.data.id) && (
        <BluecoinDetail id={itemId} closeCallback={handleCloseModal} />
      )}
      {isCreationSettingsOpen && (
        <UseCreationSettings
          data={props.data}
          closeCallback={handleCloseUseCreationSettingsModal}
          updateTable={props.updateTable}
        />
      )}
      {isDiscardConfirmDialogOpen && (
        <ConfirmDialog
          title={f({ id: 'bluecoin.confirmDialog.discard.title' })}
          cancelCallback={handleCancelDiscard}
          continueCallback={handleSubmitConfirmDialog}
          cancelButtonTitle={f({ id: 'bluecoin.confirmDialog.discard.button.cancel' })}
          continueButtonTitle={f({ id: 'bluecoin.confirmDialog.discard.button.continue' })}
        >
          {f({ id: 'bluecoin.confirmDialog.discard.text' })}
        </ConfirmDialog>
      )}
    </div>
  );
}

export default Actions;
